import { apiClient } from "@/lib/apiClient";
import { getTurnstileToken } from "@/lib/turnstile";
import { buildFilterRequestBody } from "./helpers";
import type {
  IDataFilters,
  IGetMolecularDataRequest,
  IGetMolecularDataResponse,
} from "./interfaces";

/**
 * Shared streaming-download implementation for the `/data/download*`
 * endpoints — both require a fresh Cloudflare Turnstile token, stream the
 * CSV body while reporting progress, and trigger the browser download once
 * complete.
 * @param onProgress - called with 0–100 when Content-Length is known, null otherwise
 */
async function streamDownload(
  path: string,
  body: unknown,
  onProgress: (percent: number | null) => void,
  defaultFilename: string,
): Promise<void> {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL as string) ?? "";
  const turnstileToken = await getTurnstileToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "cf-turnstile-token": turnstileToken,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(
      `Download failed: ${response.status} ${response.statusText}`,
    );
  }

  const contentLength = response.headers.get("Content-Length");
  const total = contentLength ? parseInt(contentLength, 10) : null;
  const reader = response.body!.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress(
      total ? Math.min(Math.round((received / total) * 100), 99) : null,
    );
  }

  // Parse filename from Content-Disposition header, fall back to default
  const disposition = response.headers.get("Content-Disposition");
  const filename =
    disposition
      ?.split(";")
      .find((s) => s.trim().startsWith("filename="))
      ?.split("=")[1]
      ?.replace(/['"]/g, "")
      .trim() ?? defaultFilename;

  // Final chunk assembled — trigger browser download
  const blob = new Blob(chunks as BlobPart[], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** POST /data/download — same filter body as the data endpoint. */
export const downloadMolecularData = async (
  filters: IDataFilters | undefined,
  onProgress: (percent: number | null) => void,
): Promise<void> => {
  const body = buildFilterRequestBody(filters);
  await streamDownload("/data/download", body, onProgress, "BSAD-data.csv");
};

/** POST /data/download-selected — CSV for a hand-picked set of BSA-DB IDs. */
export const downloadSelectedMolecules = async (
  ids: string[],
  onProgress: (percent: number | null) => void,
): Promise<void> => {
  await streamDownload(
    "/data/download-selected",
    { ids },
    onProgress,
    "BSAD-selected-data.csv",
  );
};

export const getMolecularData = async (
  params: IGetMolecularDataRequest = {},
): Promise<IGetMolecularDataResponse> => {
  const { filters, page, pageSize } = params;

  // filters → request body; page + pageSize → query params
  const body = buildFilterRequestBody(filters);
  const queryParams: Record<string, number> = {};
  if (page !== undefined) queryParams.page = page;
  if (pageSize !== undefined) queryParams.pageSize = pageSize;

  const { data } = await apiClient.post<IGetMolecularDataResponse>(
    "/data",
    body,
    { params: queryParams },
  );
  return data;
};
